import {
  useContext,
  useEffect,
} from 'react';
import $ from 'jquery';
import {
  ELSTokenServiceRegistrar,
  ELSURLHelper,
} from '@els/els-ui-common-react';
import {
  useHistory,
  useLocation,
} from 'react-router';
import { AppContext } from './AppProvider.component';
import { pickCookies } from '../../pages/app-link-redirect/app-link-redirect.utilities';
import { LINK_HASH_QUERY_PARAM } from '../../pages/app-link-redirect/app-link-redirect.constants';
import { REFRESH_CHECK_INTERVAL_MS, REFRESH_LEAD_TIME_MS, RoutePath } from './app.constants';
import { AppState } from './app-state.types';
import { getRefreshToken } from '../../utilities/refresh-token.utilities';
import { LoginQueryParam } from '../../pages/login/login.constants';
import { rotateRefreshTokenOrTimeout } from './AppRegistrar.utilities';
import { handleSessionStatusRequest } from './app-session.utilities';
import { trackNewRelicAction } from './app-state-actions.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';

export const AppRegistrarComponent = (): JSX.Element => {

  const history = useHistory();
  const location = useLocation();

  const {
    setAppState,
    appStateRef,
  } = useContext(AppContext);

  const rotateRefreshToken = (refreshToken: string) => {
    rotateRefreshTokenOrTimeout(refreshToken, { setAppState, appStateRef, history });
  };

  useEffect(() => {
    window.$ = $;
    window.jQuery = $;
    const cookies = pickCookies();
    setAppState((prevState): AppState => {
      return {
        ...prevState,
        appLinkCookies: cookies.linkId && cookies.token ? cookies : prevState.appLinkCookies,
        cookieCheckComplete: true
      };
    });

    ELSTokenServiceRegistrar.registerTokenTimeoutCallback(() => {
      const storedRefreshToken = getRefreshToken();
      if (storedRefreshToken) {
        rotateRefreshToken(storedRefreshToken);
      } else {
        history.push(RoutePath.TIMEOUT);
      }
    });

    // A fresh LTI launch carries its own token/refreshToken in the URL and is handled by
    // LoginPage. A fresh app-link launch carries its own token/linkId via URL params,
    // cookies, or a linkHash resolved by AppLinkRedirectPage (used when embedded in an
    // iframe, where cookies from the top-level redirect aren't available). Either way,
    // that page is about to register its own fresh token. Skip checking the previously
    // stored token here so a stale session from a prior user/tab cannot win the race:
    // read as already-expired, trigger the timeout callback, and push to the timeout
    // page before the new launch's own token gets registered.
    const queryString = location.search;
    const hasLaunchParams = Boolean(
      ELSURLHelper.getParameterByName(LoginQueryParam.TOKEN, queryString)
      || ELSURLHelper.getParameterByName(LoginQueryParam.REFRESH_TOKEN, queryString)
      || (cookies.token && cookies.linkId)
      || ELSURLHelper.getParameterByName(LINK_HASH_QUERY_PARAM, queryString)
    );

    if (!hasLaunchParams) {
      ELSTokenServiceRegistrar.initializeFromReload();
    }

    const refreshToken = getRefreshToken();

    if (!hasLaunchParams && !ELSTokenServiceRegistrar.isValidToken() && refreshToken) {
      rotateRefreshToken(refreshToken);
    }

    // The shared library schedules a single refresh timeout per token. If that one attempt is
    // delayed past expiry (iframe/tab throttling, OS sleep) the session is lost. This recurring
    // check is self-correcting: a missed tick is picked up by the next one.
    const intervalId = setInterval(() => {
      const timeToExpiration = ELSTokenServiceRegistrar.getTimeToExpiration();
      if (timeToExpiration > 0 && timeToExpiration <= REFRESH_LEAD_TIME_MS) {
        trackNewRelicAction({
          appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.TOKEN_FORCE_REFRESH_TRIGGERED,
            props: { timeToExpiration }
          }
        });
        ELSTokenServiceRegistrar.forceRefresh()
          .then(() => {
            trackNewRelicAction({
              appStateRef,
              actionProps: { action: NewRelicAnalyticsAction.TOKEN_FORCE_REFRESH_SUCCESS, props: null }
            });
          })
          .catch((error) => {
            // Transient failure, or already handled via handleTokenExpired() (401/403) inside
            // forceRefresh itself. The next tick retries while the token stays in the window.
            const debugInfo = ELSTokenServiceRegistrar.getDebugInfo();
            trackNewRelicAction({
              appStateRef,
              actionProps: {
                action: NewRelicAnalyticsAction.TOKEN_FORCE_REFRESH_FAILED,
                props: {
                  errorMessage: error?.message || String(error),
                  isRegistered: debugInfo.isRegistered,
                  isValid: debugInfo.isValid,
                  hasScheduledRefresh: debugInfo.hasScheduledRefresh,
                  timeToRefreshSeconds: debugInfo.timeToRefreshSeconds,
                  timeToExpirationSeconds: debugInfo.tokenInfo?.timeToExpirationSeconds || -1,
                  isExpired: debugInfo.tokenInfo?.isExpired || false,
                }
              }
            });
          });
      }
    }, REFRESH_CHECK_INTERVAL_MS);

    window.addEventListener('message', handleSessionStatusRequest);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('message', handleSessionStatusRequest);
    };
  }, []);

  return null;
};

const AppRegistrar = AppRegistrarComponent;
export default AppRegistrar;
